<template>
  <div class="container v-content-container">
    <div class="row">
      <div class="col-xs-12">
        <h1>
          <i class="fa fa-fw fa-lg fa-calendar"></i>Calendar

          <a
            id="print-pdf-plan"
            href="/venues/scheduler/pdf/"
            class="btn btn-v-lightgreen-white pull-right"
            data-toggle="tooltip"
            data-placement="top"
            title="PDF Plan"
            target="_blank"
          >
            <i class="fa fa-fw fa-lg fa-download"></i>
          </a>
        </h1>
      </div>
    </div>
    <div class="row calendar-container">
      <date-picker
        v-model="selectedDate"
        :format="dateFormat"
        :readonly="true"
        :placeholder="dateFormat"
        class="input"
        id="selected-date"
      >
      </date-picker>
      <FullCalendar
        id="calendar"
        ref="calendar"
        class="calendar"
        :options="calendarOptions"
        :key="componentKey"
      >
        <template v-slot:eventContent="arg">
          <b>{{ arg.timeText }}</b>
          <i>{{ arg.event.title }}</i>
        </template>
      </FullCalendar>
      <div class="row calendar-legend-container">
        <div class="col-xs-3">
          <div class="calendar-legend-single calendar-legend text-center">
            Einzel
          </div>
          <div
            class="calendar-legend-reserved-single calendar-legend text-center"
          >
            Reservierung Einzel
          </div>
        </div>
        <div class="col-xs-3">
          <div class="calendar-legend-subscription calendar-legend text-center">
            Abo
          </div>
          <div
            class="calendar-legend-reserved-subscription calendar-legend text-center"
          >
            Reservierung Abo
          </div>
        </div>
        <div class="col-xs-3">
          <div class="calendar-legend-course calendar-legend text-center">
            Kurs
          </div>
          <div class="calendar-legend-holiday calendar-legend text-center">
            Feiertag
          </div>
        </div>
        <div class="col-xs-3">
          <div class="calendar-legend-timeframe calendar-legend text-center">
            Geschlossen / Gesperrt
          </div>
          <div class="text-center" style="padding-top: 2px;">
            <a id="ccTrue" class="custom-colors">Kundenfarben aktivieren</a>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import Api from "@/api.js";

import FullCalendar from "@fullcalendar/vue";
import dayGridPlugin from "@fullcalendar/daygrid";
import timeGridPlugin from "@fullcalendar/timegrid";
import interactionPlugin from "@fullcalendar/interaction";
import luxonPlugin from "@fullcalendar/luxon";
import resourceTimelinePlugin from "@fullcalendar/resource-timeline";
import resourceTimegridPlugin from "@fullcalendar/resource-timegrid";

import DatePicker from "vue2-datepicker";
import "vue2-datepicker/index.css";
import "vue2-datepicker/locale/de";

let eventGuid = 0;
let todayStr = new Date().toISOString().replace(/T.*$/, ""); // YYYY-MM-DD of today
const schedulerLicenseKey = process.env.FULL_CALENDAR_LICENSE_KEY
  ? process.env.FULL_CALENDAR_LICENSE_KEY
  : "CC-Attribution-NonCommercial-NoDerivatives";

const INITIAL_EVENTS = [
  {
    id: createEventId(),
    title: "All-day event",
    start: todayStr
  },
  {
    id: createEventId(),
    title: "Timed event",
    start: todayStr + "T12:00:00"
  }
];

function createEventId() {
  return String(eventGuid++);
}

export default {
  components: {
    FullCalendar, // make the <FullCalendar> tag available
    DatePicker
  },

  data: function() {
    return {
      componentKey: 0,
      facilities: [],
      loading: false,
      selectedDate: new Date(),
      dateFormat: "DD.MM.YYYY",
      CalendarApi: null,
      calendarOptions: {
        schedulerLicenseKey,
        plugins: [
          dayGridPlugin,
          timeGridPlugin,
          interactionPlugin, // needed for dateClick
          luxonPlugin,
          resourceTimelinePlugin,
          resourceTimegridPlugin
        ],
        customButtons: {
          selectVenueButton: {
            text: "Select venues",
            click: function() {}
          }
        },
        headerToolbar: {
          left: "today prev,next selectVenueButton",
          center: "title",
          right:
            "resourceTimelineDay5,resourceTimelineDay15,resourceTimelineDay,resourceTimelineDay60 resourceTimelineWeek resourceTimeGridDay"
        },
        views: {
          resourceTimelineDay: {
            type: "resourceTimeline",
            titleFormat: "{EEEE, {dd}. LLLL} yyyy",
            buttonText: "30m",
            slotDuration: "00:30",
            slotLabelFormat: ["HH:mm"]
          },
          resourceTimelineDay5: {
            type: "resourceTimeline",
            titleFormat: "{EEEE, {dd}. LLLL} yyyy",
            buttonText: "5m",
            slotDuration: "00:05",
            slotLabelFormat: ["HH:mm"]
          },
          resourceTimelineDay15: {
            type: "resourceTimeline",
            titleFormat: "{EEEE, {dd}. LLLL} yyyy",
            buttonText: "15m",
            slotDuration: "00:15",
            slotLabelFormat: ["HH:mm"]
          },
          resourceTimelineDay60: {
            type: "resourceTimeline",
            titleFormat: "{EEEE, {dd}. LLLL} yyyy",
            buttonText: "1h",
            slotDuration: "01:00",
            slotLabelFormat: ["HH:mm"]
          },
          resourceTimelineWeek: {
            type: "resourceTimeline",
            titleFormat: "{{d}. LLLL}. yyyy",
            slotDuration: "01:00",
            slotLabelFormat: "HH"
          },
          resourceTimeGridDay: {
            allDaySlot: true,
            buttonText: "Horizontal",
            titleFormat: "{EEEE, {dd}. LLLL} yyyy",
            slotLabelFormat: "HH"
          }
        },
        datesAboveResources: true,
        resourceGroupField: "",
        resources: [],
        height: "auto",
        allDaySlot: false,
        firstDay: 1,
        dayHeaders: true,
        dayHeaderFormat: "EEE d.M",
        datesSet: this.handleChangeDate,
        slotMinTime: "07:00:00",
        slotMaxTime: "23:00:00",
        slotLabelFormat: "HH:mm",
        eventTimeFormat: "HH:mm",
        slotDuration: "00:30:00",
        initialView: "resourceTimelineDay",
        initialEvents: INITIAL_EVENTS, // alternatively, use the `events` setting to fetch from a feed
        editable: true,
        droppable: true,
        selectable: true,
        selectMirror: true,
        dayMaxEvents: true,
        weekends: true,
        select: this.handleDateSelect,
        eventClick: this.handleEventClick,
        eventsSet: this.handleEvents,
        eventDragStart: this.handleEventDragStart,
        eventDrop: this.handleEventDrop,
        eventReceive: this.handleEventDrop,
        resourceAreaHeaderContent: "Location"
        /* you can update a remote database when these fire:
          eventAdd:
          eventChange:
          eventRemove:
          */
      },
      currentEvents: []
    };
  },

  beforeRouteEnter(to, from, next) {
    next(vm => {
      vm.getFacilities(() => {
        //
      });
    });
  },

  beforeRouteUpdate(to, from, next) {
    this.getFacilities(() => {
      //
    });
    next();
  },

  mounted() {
    document
      .getElementsByClassName("fc-toolbar-chunk")[0]
      .prepend(document.getElementById("selected-date"));

    this.CalendarApi = this.$refs.calendar.$options.calendar;
  },

  methods: {
    getFacilities(successCallback) {
      const endpoint = this.$partnerEndpoint("facilities/simplified");

      Api()
        .get(endpoint)
        .then(response => {
          this.facilities = response.data.data;
          response.data.data.map(facility => {
            facility.venues &&
              facility.venues.map(venue => {
                this.calendarOptions.resources.push({
                  id: venue.id,
                  title: venue.name,
                  facility: facility.name
                });
              });
          });
          this.calendarOptions.resourceGroupField = "facility";
          if (typeof successCallback == "function") {
            successCallback();
          }
        })
        .catch(error => {
          console.log(error);
        });
    },

    handleDateSelect(selectInfo) {
      let title = prompt("Please enter a new title for your event");
      let calendarApi = selectInfo.view.calendar;

      calendarApi.unselect(); // clear date selection

      if (title) {
        calendarApi.addEvent({
          id: createEventId(),
          title,
          start: selectInfo.startStr,
          end: selectInfo.endStr,
          allDay: selectInfo.allDay
        });
      }
    },

    handleEventClick(clickInfo) {
      if (
        confirm(
          `Are you sure you want to delete the event "${clickInfo.event.title}"`
        )
      ) {
        clickInfo.event.remove();
      }
    },

    handleEvents(events) {
      this.currentEvents = events;
    },

    handleEventDragStart() {
      this.CalendarApi.setOption("snapDuration", "00:05:00");
    },

    handleEventDrop() {
      this.CalendarApi.setOption("snapDuration", "00:30:00");
    },

    handleChangeDate(dateInfo) {
      this.selectedDate = dateInfo.start;
    },

    forceRerender() {
      this.componentKey += 1;
    }
  },

  watch: {
    selectedDate(newDate) {
      newDate && this.CalendarApi.gotoDate(newDate);
    }
  }
};
</script>

<style>
.fc-toolbar-title {
  margin: 0;
  font-size: 16px;
}

ul {
  margin: 0;
  padding: 0 0 0 1.5em;
}

li {
  margin: 1.5em 0;
  padding: 0;
}

/* used for event dates/times */
b {
  margin-right: 3px;
}

.calendar-container {
  flex-grow: 1;
  padding: 0 2rem;
}

#selected-date {
  width: 110px !important;
}

.mx-datepicker .mx-icon-calendar {
  display: none;
}

.mx-datepicker .mx-input {
  padding: 6px 6px 6px 10px;
}

/* the calendar root */
.fc {
  flex-grow: 1;
}

.fc .fc-toolbar {
  align-items: flex-start;
}

.fc .fc-toolbar-title {
  font-size: 1.2em;
}

.fc .fc-toolbar-chunk {
  display: flex;
}

.fc .fc-button {
  border: none;
  text-transform: capitalize;
}

.fc-button:hover,
.fc-button:active,
.fc-button-active {
  opacity: 0.65;
}

.fc-button:hover,
.fc-button:active,
.fc-button:focus {
  border: none;
  outline: none;
}

.fc-button:focus {
  box-shadow: none !important;
}

.fc-button.fc-button-primary,
.fc-button.fc-button-primary:hover,
.fc-button.fc-button-primary:active {
  background-color: #0088cc !important;
}

.fc-prev-button,
.fc-next-button {
  padding: 0.4em 0.9em !important;
}
</style>
